import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { useContext, useEffect, useState } from 'react';
import {
  ActiveIndex,
  AssistantWidgetContext,
  setActiveIndex,
  setChatPopoverAnchor,
  setIsAvailable,
  setIsShowOnboard,
  setIsShowUpgrade,
} from 'presentations/reducers/AssistantWidget';
import { patch } from 'infras/rest_client';
import { apiPath } from 'presentations/routes/path';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import { useRequest } from 'presentations/views/hooks/request';
import { AssistantSettingItem } from 'integrations/assistant/presentations/api/schema/assistant_setting';
import { Status } from 'integrations/assistant/models/setting/status';
import { setEvent } from 'presentations/views/helpers/analytics';

interface Props {
  isOpen: boolean;
}

export const useChatPopover = (props: Props) => {
  const { state, dispatch } = useContext(AssistantWidgetContext);
  const portalContext = useContext(DashboardPortalContext);
  const t = translateInComponent([translations]);

  const [isAvailableLoading, setIsAvailableLoading] = useState<boolean>(false);
  const [defaultConversationMessage, setDefaultConversationMessage] = useState<
    string | null
  >(null);

  const settingRequest = useRequest(
    props.isOpen ? apiPath(`/v1/integrations/assistant/settings`) : null,
  );
  const setting: AssistantSettingItem | null = settingRequest.data;

  useEffect(() => {
    if (!setting) return;
    const status = Boolean(setting.status);
    dispatch(setIsAvailable(status));
    dispatch(setIsShowOnboard(!status && props.isOpen));
  }, [setting?.status, props.isOpen]);

  useEffect(() => {
    if (state.activeIndex === ActiveIndex.Conversation) {
      setDefaultConversationMessage(null);
    }
  }, [state.activeIndex]);

  const onAvailable = async () => {
    const url = `/v1/integrations/assistant/settings/update`;
    const data = { status: Status.Enabled };
    setIsAvailableLoading(true);
    const result = await patch(apiPath(url), { data });
    if (!result.isSuccess) {
      portalContext.dispatch(setErrorMessage(t('Fail to activate')));
      setIsAvailableLoading(false);
      return;
    }
    dispatch(setIsShowOnboard(false));
    dispatch(setIsAvailable(true));
    setEvent('activate chat assistant');
  };

  const onClickBack = () => {
    dispatch(setActiveIndex(ActiveIndex.Top));
    setEvent('click back chat assistant view', {
      activeIndex: state.activeIndex,
    });
  };

  const onClickPresetView = (activeIndex: ActiveIndex) => {
    dispatch(setActiveIndex(activeIndex));
    setEvent('show chat assistant view', {
      activeIndex,
    });
  };

  const onClose = () => {
    dispatch(setChatPopoverAnchor(null));
  };

  const onCloseUpgrade = () => {
    dispatch(setIsShowUpgrade(false));
    onClose();
  };

  const onCloseOnboard = () => {
    dispatch(setIsShowOnboard(false));
    onClose();
  };

  const onSubmitMessage = (content: string) => {
    dispatch(setActiveIndex(ActiveIndex.Conversation));
    setDefaultConversationMessage(content);
  };

  return {
    state: {
      activeIndex: state.activeIndex,
      isShowOnboard: state.isShowOnboard,
      isShowUpgrade: state.isShowUpgrade,
      isAvailable: state.isAvailable,
      isAvailableLoading,
      defaultConversationMessage,
    },
    actions: {
      onClickBack,
      onClickPresetView,
      onSubmitMessage,
      onClose,
      onCloseUpgrade,
      onCloseOnboard,
      onAvailable,
    },
  };
};
